import { StrictMode } from 'react';

import { installValidationMessages } from '@daka/core';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { createRoot } from 'react-dom/client';

import { ApiActivitySpinner } from '@/components/api-activity-spinner';
import { AppErrorBoundary } from '@/components/ErrorBoundary';
// Registering modules at import time means navigation is available on first
// paint — no effect, no loading state for something statically known.
import '@/host/modules';
import { I18nProvider } from '@/i18n/provider';
import '@/index.css';
import { routeTree } from '@/routeTree.gen';

// Every zod issue is a message key in the browser too — the same text the API would answer with.
installValidationMessages();

document.title = import.meta.env.VITE_APP_NAME?.trim() || 'Order Writer';

const queryClient = new QueryClient({
   defaultOptions: {
      queries: {
         staleTime: 30_000,
         // An expired session or a 404 will not fix itself by retrying.
         retry: 1,
         refetchOnWindowFocus: false
      }
   }
});

const router = createRouter({
   routeTree,
   context: { queryClient },
   defaultPreload: 'intent'
});

declare module '@tanstack/react-router' {
   interface Register {
      router: typeof router;
   }
}

const container = document.getElementById('root');
if (container) {
   createRoot(container).render(
      <StrictMode>
         <QueryClientProvider client={queryClient}>
            <I18nProvider>
               <ApiActivitySpinner />
               <AppErrorBoundary>
                  <RouterProvider router={router} />
               </AppErrorBoundary>
            </I18nProvider>
         </QueryClientProvider>
      </StrictMode>
   );
} else {
   console.error('[web] Root element #root not found');
}
